import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import {
  fetchProductColumns,
  fetchProductFilterOptions,
  fetchProductFilters,
} from '../services/product-metadata.service';
import type { AppliedProductFilters, ProductFiltersParams } from '../types/product-metadata.types';

/** Page size for lazy filter-option fetches — backend caps at 100. */
const FILTER_OPTIONS_PAGE_SIZE = 100;

/**
 * Product column catalogue for a connector. Pass `null` to keep the query
 * disabled (e.g. before the wizard's `connectorCode` resolves). Cached
 * generously (10 min) — the schema rarely changes within a session.
 */
export function useProductColumns(connectorCode: string | null) {
  return useQuery({
    queryKey: queryKeys.products.columns(connectorCode ?? ''),
    queryFn: () => {
      if (!connectorCode) throw new Error('connectorCode is required');
      return fetchProductColumns(connectorCode);
    },
    enabled: !!connectorCode,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Dynamic filter definitions for the product-scope drawer. Pass `null` to
 * keep the query disabled until `connectionId` + `connectorCode` resolve.
 * Query key includes `appliedFilters` so the backend-returned option lists
 * refresh as the user narrows the selection.
 */
export function useProductFilters(params: ProductFiltersParams | null) {
  return useQuery({
    queryKey: queryKeys.products.filters(
      params?.connectionId ?? '',
      (params?.appliedFilters ?? {}) as Record<string, unknown>,
      params?.syncId ?? null,
    ),
    queryFn: () => {
      if (!params) throw new Error('params is required');
      return fetchProductFilters(params);
    },
    enabled: params !== null,
    // A sync's filter options don't change; refetch only after a while, not on every mount.
    staleTime: 10 * 60_000,
  });
}

/** Args for {@link useProductFilterOptions} — `enabled` gates the lazy fetch to expanded rows. */
export interface UseProductFilterOptionsArgs {
  connectorCode: string | null;
  connectionId: string | null;
  filterKey: string;
  /** Debounced search text — empty string fetches the unfiltered first page. */
  search: string;
  /** Narrowing context (the drawer's seed snapshot); backend self-excludes `filterKey`. */
  appliedFilters: AppliedProductFilters;
  /** Selected sync/revision snapshot to scope options to (null = latest). */
  syncId?: string | null;
  /** Only fetch once the accordion is open and the connection resolved. */
  enabled: boolean;
}

/**
 * Lazy, searchable, infinite-scroll option values for a single paginated
 * filter key. Each page returns up to {@link FILTER_OPTIONS_PAGE_SIZE} values;
 * `getNextPageParam` advances while the backend reports `hasNextPage`. Disabled
 * until the row is expanded so collapsed filters cost nothing.
 */
export function useProductFilterOptions({
  connectorCode,
  connectionId,
  filterKey,
  search,
  appliedFilters,
  syncId = null,
  enabled,
}: UseProductFilterOptionsArgs) {
  return useInfiniteQuery({
    queryKey: queryKeys.products.filterOptions(
      connectionId ?? '',
      filterKey,
      search,
      appliedFilters as Record<string, unknown>,
      syncId,
    ),
    queryFn: ({ pageParam }) => {
      if (!connectorCode || !connectionId) throw new Error('connection is required');
      return fetchProductFilterOptions({
        connectorCode,
        connectionId,
        key: filterKey,
        search,
        appliedFilters,
        syncId,
        page: pageParam,
        size: FILTER_OPTIONS_PAGE_SIZE,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNextPage ? lastPage.pagination.page + 1 : undefined,
    enabled: enabled && !!connectorCode && !!connectionId,
  });
}
